import React from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, Trash2, Upload } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelectDropdown, type AppSelectOption } from '../../../components/AppSelectDropdown';
import type { CompanyResponsiblePerson, CompanySettings } from '../../../types';
import { companyLogoSrc, instanceIdFromPathname } from '../../../utils/companyLogo';
import { normalizeGermanStateCode } from '../../../utils/germanStates';
import {
  buildCompanySavePayload,
  companyCardIsDirty,
  companySettingsFromApi,
  DIRECTOR_ROLE_OPTIONS,
  emptyCompanyForm,
  GERMAN_STATE_LABEL_BY_CODE,
  GERMAN_STATE_OPTIONS,
  type CompanySettingsCardId,
} from '../utils/companySettingsForm';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TABLE_HEADER,
} from '../utils/settingsSectionStyles';

function CompanyReadField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className={SETTINGS_TABLE_HEADER}>{label}</p>
      <div className="mt-1 text-sm text-app-text">{children}</div>
    </div>
  );
}

export function CompanySettingsSection() {
  const location = useLocation();
  const urlInstanceId = React.useMemo(
    () => instanceIdFromPathname(location.pathname),
    [location.pathname]
  );

  const [companyForm, setCompanyForm] = React.useState(emptyCompanyForm);
  const [editingCompanyCard, setEditingCompanyCard] = React.useState<CompanySettingsCardId | null>(null);
  const companyCardRevert = React.useRef<Partial<typeof emptyCompanyForm> | null>(null);
  const [companyLoaded, setCompanyLoaded] = React.useState(false);
  const [companyLoadError, setCompanyLoadError] = React.useState<string | null>(null);
  const [companySaving, setCompanySaving] = React.useState(false);
  const [companySavedCard, setCompanySavedCard] = React.useState<CompanySettingsCardId | null>(null);
  const [companyError, setCompanyError] = React.useState<string | null>(null);
  const [hasLogo, setHasLogo] = React.useState(false);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoBust, setLogoBust] = React.useState(0);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  const directorRoleDropdownOptions = React.useMemo((): AppSelectOption[] => {
    const rows: AppSelectOption[] = [{ value: '', label: 'Bitte wählen …' }];
    DIRECTOR_ROLE_OPTIONS.forEach((o) => rows.push({ value: o, label: o }));
    const raw = companyForm.director_role;
    const t = raw.trim();
    if (t && !DIRECTOR_ROLE_OPTIONS.includes(t)) {
      const exists = rows.some((r) => r.value === raw);
      if (!exists) rows.push({ value: raw, label: `${t} (bisheriger Eintrag)` });
    }
    return rows;
  }, [companyForm.director_role]);

  React.useEffect(() => {
    let cancelled = false;
    setCompanyLoadError(null);
    fetch('/api/settings/company')
      .then(async (r) => {
        if (!r.ok) {
          const err = (await r.json().catch(() => ({}))) as { error?: string };
          throw new Error(err.error || 'Unternehmensdaten konnten nicht geladen werden.');
        }
        return r.json() as Promise<CompanySettings>;
      })
      .then((d) => {
        if (cancelled) return;
        setHasLogo(Boolean(d.has_logo));
        setCompanyForm(companySettingsFromApi(d));
        setCompanyLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setCompanyLoadError(e instanceof Error ? e.message : 'Unternehmensdaten konnten nicht geladen werden.');
        setCompanyLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveCompanySettings = async (): Promise<boolean> => {
    if (!editingCompanyCard) return false;
    if (companyLoadError) {
      setCompanyError('Unternehmensdaten sind nicht geladen — bitte Seite neu laden.');
      return false;
    }
    setCompanySaving(true);
    setCompanySavedCard(null);
    setCompanyError(null);
    try {
      const payload = buildCompanySavePayload(editingCompanyCard, companyForm);
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      const saved = d as CompanySettings;
      setHasLogo(Boolean(saved.has_logo));
      setCompanyForm(companySettingsFromApi(saved));
      setCompanySavedCard(editingCompanyCard);
      setTimeout(() => setCompanySavedCard(null), 2500);
      return true;
    } catch (e) {
      setCompanyError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
      return false;
    } finally {
      setCompanySaving(false);
    }
  };

  const beginCompanyCardEdit = (id: CompanySettingsCardId) => {
    if (id === 'unternehmen') {
      companyCardRevert.current = {
        company_name: companyForm.company_name,
        street: companyForm.street,
        house_number: companyForm.house_number,
        postal_code: companyForm.postal_code,
        city: companyForm.city,
        country_code: companyForm.country_code,
        state_code: companyForm.state_code,
      };
    } else if (id === 'kontakt') {
      companyCardRevert.current = {
        phone: companyForm.phone,
        email: companyForm.email,
        website: companyForm.website,
      };
    } else if (id === 'rechtlich') {
      companyCardRevert.current = {
        director_role: companyForm.director_role,
        responsible_persons: companyForm.responsible_persons.map((p) => ({ ...p })),
        vat_id: companyForm.vat_id,
        tax_number: companyForm.tax_number,
        registry_court: companyForm.registry_court,
        commercial_register_number: companyForm.commercial_register_number,
      };
      setCompanyForm((f) =>
        f.responsible_persons.length === 0 ? { ...f, responsible_persons: [{ name: '' }] } : f
      );
    } else if (id === 'bank') {
      companyCardRevert.current = {
        bank_name: companyForm.bank_name,
        bank_account_holder: companyForm.bank_account_holder,
        iban: companyForm.iban,
        bic: companyForm.bic,
      };
    } else {
      companyCardRevert.current = null;
    }
    setCompanySavedCard(null);
    setEditingCompanyCard(id);
  };

  const updateResponsiblePerson = (index: number, patch: Partial<CompanyResponsiblePerson>) => {
    setCompanyForm((f) => ({
      ...f,
      responsible_persons: f.responsible_persons.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));
  };

  const addResponsiblePerson = () => {
    setCompanyForm((f) => ({
      ...f,
      responsible_persons: [...f.responsible_persons, { name: '' }],
    }));
  };

  const removeResponsiblePerson = (index: number) => {
    setCompanyForm((f) => ({
      ...f,
      responsible_persons:
        f.responsible_persons.length <= 1
          ? [{ name: '' }]
          : f.responsible_persons.filter((_, i) => i !== index),
    }));
  };

  const cancelCompanyCardEdit = () => {
    if (companyCardRevert.current) {
      setCompanyForm((f) => ({ ...f, ...companyCardRevert.current }));
    }
    companyCardRevert.current = null;
    setEditingCompanyCard(null);
  };

  const saveCompanyCardAndClose = async () => {
    const ok = await saveCompanySettings();
    if (ok) {
      companyCardRevert.current = null;
      setEditingCompanyCard(null);
    }
  };

  const dash = (s: string) => (s.trim() ? s : '—');

  const companyCardDirty =
    editingCompanyCard != null &&
    companyCardIsDirty(editingCompanyCard, companyForm, companyCardRevert.current);

  const renderCardActions = (id: CompanySettingsCardId) => {
    const editing = editingCompanyCard === id;
    return (
      <>
        <SettingsCardSavedStatus saved={companySavedCard === id && !editing} />
        {editing ? (
          <>
            <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={cancelCompanyCardEdit}>
              Abbrechen
            </button>
            <button
              type="button"
              className={SETTINGS_BTN_PRIMARY}
              disabled={companySaving || !companyCardDirty}
              onClick={() => void saveCompanyCardAndClose()}
            >
              {companySaving ? 'Speichert…' : 'Speichern'}
            </button>
          </>
        ) : (
          <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={() => beginCompanyCardEdit(id)}>
            Bearbeiten
          </button>
        )}
      </>
    );
  };

  const cardErrorBanner = (id: CompanySettingsCardId) =>
    editingCompanyCard === id && companyError ? (
      <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{companyError}</div>
    ) : null;

  const formatCompanyAddress = () => {
    const line1 = [companyForm.street, companyForm.house_number].filter((x) => x.trim()).join(' ');
    const line2 = [companyForm.postal_code, companyForm.city].filter((x) => x.trim()).join(' ');
    if (!line1 && !line2) return '—';
    return [line1, line2].filter(Boolean).join('\n');
  };

  const onLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoUploading(true);
    setCompanyError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/settings/company/logo', { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Logo-Upload fehlgeschlagen');
      setHasLogo(true);
      setLogoBust((n) => n + 1);
    } catch (err) {
      setCompanyError(err instanceof Error ? err.message : 'Logo-Upload fehlgeschlagen');
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    setCompanyError(null);
    try {
      const res = await fetch('/api/settings/company/logo', { method: 'DELETE' });
      if (!res.ok) throw new Error('Logo konnte nicht entfernt werden');
      setHasLogo(false);
      setLogoBust((n) => n + 1);
    } catch (err) {
      setCompanyError(err instanceof Error ? err.message : 'Fehler beim Entfernen');
    }
  };

  const pageHeader = (
    <div className="px-4 pt-4 md:px-0 md:pt-0">
      <h1 className="text-lg font-bold text-app-text tracking-tight">Unternehmensangaben</h1>
      <p className="mt-1 text-sm text-app-text-muted">Erscheint auf Angeboten und Rechnungen.</p>
    </div>
  );

  const logoColumn = (editing: boolean) => (
    <div
      className={`w-full sm:w-56 flex flex-col ${
        editing ? 'lg:self-start' : 'h-full'
      }`}
    >
      <p className={SETTINGS_TABLE_HEADER}>Logo</p>
      <div
        className={`mt-1.5 w-full sm:w-56 flex items-center justify-center overflow-hidden rounded-md border border-app-border bg-app-surface ${
          editing ? 'h-36' : 'h-28 lg:h-auto lg:flex-1 lg:min-h-[7rem]'
        }`}
      >
        {hasLogo ? (
          <img
            src={companyLogoSrc(urlInstanceId, logoBust)}
            alt="Firmenlogo"
            className="max-h-full max-w-full object-contain p-2"
          />
        ) : (
          <span className="text-xs text-slate-400 px-3 text-center">Noch kein Logo</span>
        )}
      </div>
      {editing ? (
        <div className="mt-3 flex flex-col gap-2">
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => void onLogoFile(e)}
          />
          <button
            type="button"
            disabled={logoUploading}
            onClick={() => logoInputRef.current?.click()}
            className={SETTINGS_BTN_OUTLINE}
          >
            <Upload className="w-4 h-4 text-pipedrive-blue" />
            {logoUploading ? 'Wird hochgeladen…' : 'Logo hochladen'}
          </button>
          {hasLogo ? (
            <button type="button" onClick={() => void removeLogo()} className={SETTINGS_BTN_DANGER_OUTLINE}>
              <Trash2 className="w-4 h-4" />
              Logo entfernen
            </button>
          ) : null}
          <p className="text-xs text-app-text-subtle">PNG oder JPG, max. 5&nbsp;MB.</p>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      {pageHeader}
      {!companyLoaded ? (
        <SettingsCard title="Unternehmen">
          <InlineLoadingState label="Lade Unternehmensdaten…" />
        </SettingsCard>
      ) : companyLoadError ? (
        <SettingsCard title="Unternehmen">
          <p className="text-sm text-red-700">{companyLoadError}</p>
        </SettingsCard>
      ) : (
        <>
          <SettingsCard title="Unternehmen" actions={renderCardActions('unternehmen')}>
            {cardErrorBanner('unternehmen')}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5 lg:items-stretch">
              {editingCompanyCard === 'unternehmen' ? (
                <div className="min-w-0 flex-1 space-y-4">
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Name (mit Rechtsform)</label>
                    <input
                      type="text"
                      value={companyForm.company_name}
                      onChange={(e) => setCompanyForm((f) => ({ ...f, company_name: e.target.value }))}
                      placeholder="z. B. Mustermann GmbH"
                      className={SETTINGS_INPUT_CLASS}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Straße</label>
                      <input
                        type="text"
                        value={companyForm.street}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, street: e.target.value }))}
                        placeholder="Musterstraße"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                    <div>
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Hausnummer</label>
                      <input
                        type="text"
                        value={companyForm.house_number}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, house_number: e.target.value }))}
                        placeholder="12a"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>PLZ</label>
                      <input
                        type="text"
                        value={companyForm.postal_code}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, postal_code: e.target.value }))}
                        placeholder="10115"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Ort</label>
                      <input
                        type="text"
                        value={companyForm.city}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, city: e.target.value }))}
                        placeholder="Berlin"
                        className={SETTINGS_INPUT_CLASS}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Land</label>
                      <input type="text" value="Deutschland" className={SETTINGS_INPUT_CLASS} disabled />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Bundesland / Region</label>
                      <AppSelectDropdown
                        value={companyForm.state_code}
                        onChange={(value) =>
                          setCompanyForm((f) => ({
                            ...f,
                            state_code: normalizeGermanStateCode(value) ?? '',
                          }))
                        }
                        options={GERMAN_STATE_OPTIONS}
                        className="w-full"
                        placeholder="Bitte wählen …"
                        triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="min-w-0 flex-1 space-y-4">
                  <CompanyReadField label="Name (mit Rechtsform)">
                    {dash(companyForm.company_name)}
                  </CompanyReadField>
                  <CompanyReadField label="Anschrift">
                    <span className="whitespace-pre-line">{formatCompanyAddress()}</span>
                  </CompanyReadField>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 max-w-xl">
                    <CompanyReadField label="Land">Deutschland</CompanyReadField>
                    <CompanyReadField label="Bundesland / Region">
                      {dash(GERMAN_STATE_LABEL_BY_CODE.get(companyForm.state_code) ?? '')}
                    </CompanyReadField>
                  </div>
                </div>
              )}
              {logoColumn(editingCompanyCard === 'unternehmen')}
            </div>
          </SettingsCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
          <SettingsCard className="h-full" title="Kontaktdaten" actions={renderCardActions('kontakt')}>
            {cardErrorBanner('kontakt')}
            {editingCompanyCard === 'kontakt' ? (
              <div className="space-y-4">
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>E-Mail</label>
                  <input
                    type="email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="info@firma.de"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Telefon</label>
                  <input
                    type="tel"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+49 …"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Website</label>
                  <input
                    type="url"
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, website: e.target.value }))}
                    placeholder="https://www.firma.de"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-y-4">
                <CompanyReadField label="E-Mail">
                  {companyForm.email.trim() ? (
                    <a href={`mailto:${companyForm.email}`} className="text-pipedrive-blue hover:underline">
                      {companyForm.email}
                    </a>
                  ) : (
                    '—'
                  )}
                </CompanyReadField>
                <CompanyReadField label="Telefon">{dash(companyForm.phone)}</CompanyReadField>
                <CompanyReadField label="Website">
                  {companyForm.website.trim() ? (
                    <a
                      href={
                        companyForm.website.trim().startsWith('http')
                          ? companyForm.website.trim()
                          : `https://${companyForm.website.trim()}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pipedrive-blue hover:underline break-all"
                    >
                      {companyForm.website.trim()}
                    </a>
                  ) : (
                    '—'
                  )}
                </CompanyReadField>
              </div>
            )}
          </SettingsCard>

          <SettingsCard className="h-full" title="Bank" actions={renderCardActions('bank')}>
            {cardErrorBanner('bank')}
            {editingCompanyCard === 'bank' ? (
              <div className="space-y-4">
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Bankname</label>
                  <input
                    type="text"
                    value={companyForm.bank_name}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, bank_name: e.target.value }))}
                    placeholder="z. B. Sparkasse Berlin"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Kontoinhaber</label>
                  <input
                    type="text"
                    value={companyForm.bank_account_holder}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, bank_account_holder: e.target.value }))}
                    placeholder="Name laut Kontoauszug"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>IBAN</label>
                  <input
                    type="text"
                    value={companyForm.iban}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, iban: e.target.value }))}
                    placeholder="DE89 …"
                    className={`${SETTINGS_INPUT_CLASS} font-mono`}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>BIC</label>
                  <input
                    type="text"
                    value={companyForm.bic}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, bic: e.target.value }))}
                    placeholder="COBADEFFXXX"
                    className={`${SETTINGS_INPUT_CLASS} font-mono uppercase`}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-y-4">
                <CompanyReadField label="Bankname">{dash(companyForm.bank_name)}</CompanyReadField>
                <CompanyReadField label="Kontoinhaber">{dash(companyForm.bank_account_holder)}</CompanyReadField>
                <CompanyReadField label="IBAN">
                  <span className="font-mono">{dash(companyForm.iban)}</span>
                </CompanyReadField>
                <CompanyReadField label="BIC">
                  <span className="font-mono uppercase">{dash(companyForm.bic)}</span>
                </CompanyReadField>
              </div>
            )}
          </SettingsCard>
          </div>

          <SettingsCard title="Rechtliches" actions={renderCardActions('rechtlich')}>
            {cardErrorBanner('rechtlich')}
            {editingCompanyCard === 'rechtlich' ? (
              <div className="space-y-6">
                <div>
                  <label htmlFor="settings-director-role" className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>
                    Verantwortliche Person (Typ)
                  </label>
                  <p className="text-xs text-app-text-subtle mb-2">Gilt für alle unten genannten Personen.</p>
                  <AppSelectDropdown
                    id="settings-director-role"
                    value={companyForm.director_role}
                    onChange={(v) => setCompanyForm((f) => ({ ...f, director_role: v }))}
                    options={directorRoleDropdownOptions}
                    placeholder="Bitte wählen …"
                    className="w-full"
                    triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                  />
                </div>
                <div>
                  <p className={`${SETTINGS_FIELD_LABEL} mb-3`}>Namen</p>
                  <div className="space-y-4">
                    {companyForm.responsible_persons.map((person, index) => (
                      <div
                        key={index}
                        className="rounded-lg border border-app-border bg-app-surface-muted/80 p-4 flex flex-col gap-3 sm:flex-row sm:items-end"
                      >
                        <div className="flex-1 w-full">
                          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Name</label>
                          <input
                            type="text"
                            value={person.name}
                            onChange={(e) => updateResponsiblePerson(index, { name: e.target.value })}
                            placeholder="Vor- und Nachname"
                            className={SETTINGS_INPUT_CLASS}
                          />
                        </div>
                        <div className="flex justify-end sm:pb-0.5">
                          <button
                            type="button"
                            onClick={() => removeResponsiblePerson(index)}
                            className="text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            Entfernen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addResponsiblePerson}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-pipedrive-blue hover:underline"
                  >
                    <Plus className="w-4 h-4" />
                    Weitere Person
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>USt-ID</label>
                    <input
                      type="text"
                      value={companyForm.vat_id}
                      onChange={(e) => setCompanyForm((f) => ({ ...f, vat_id: e.target.value }))}
                      placeholder="DE123456789"
                      className={SETTINGS_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Steuernummer</label>
                    <input
                      type="text"
                      value={companyForm.tax_number}
                      onChange={(e) => setCompanyForm((f) => ({ ...f, tax_number: e.target.value }))}
                      placeholder="z. B. 12/123/12345"
                      className={SETTINGS_INPUT_CLASS}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Handelsregisternummer</label>
                  <input
                    type="text"
                    value={companyForm.commercial_register_number}
                    onChange={(e) =>
                      setCompanyForm((f) => ({ ...f, commercial_register_number: e.target.value }))
                    }
                    placeholder="z. B. HRB 12345"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Zuständiges Amtsgericht</label>
                  <input
                    type="text"
                    value={companyForm.registry_court}
                    onChange={(e) => setCompanyForm((f) => ({ ...f, registry_court: e.target.value }))}
                    placeholder="z. B. Amtsgericht Berlin-Charlottenburg"
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                <CompanyReadField label="Verantwortliche Person (Typ)">
                  {dash(companyForm.director_role)}
                </CompanyReadField>
                <CompanyReadField label="Verantwortliche Personen">
                  {companyForm.responsible_persons.filter((p) => p.name.trim()).length === 0 ? (
                    '—'
                  ) : (
                    <ul className="space-y-0.5">
                      {companyForm.responsible_persons
                        .filter((p) => p.name.trim())
                        .map((person, index) => (
                          <li key={index}>{person.name.trim()}</li>
                        ))}
                    </ul>
                  )}
                </CompanyReadField>
                <CompanyReadField label="USt-ID">{dash(companyForm.vat_id)}</CompanyReadField>
                <CompanyReadField label="Steuernummer">{dash(companyForm.tax_number)}</CompanyReadField>
                <CompanyReadField label="Handelsregisternummer">
                  {dash(companyForm.commercial_register_number)}
                </CompanyReadField>
                <CompanyReadField label="Zuständiges Amtsgericht">
                  {dash(companyForm.registry_court)}
                </CompanyReadField>
              </div>
            )}
          </SettingsCard>
        </>
      )}
    </div>
  );
}
